import { formatDate } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  FilterConfig,
  FilterResult,
  FiltersConfig,
  FilterTypes,
  NestedFilterOption,
} from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { DataSeries } from '@next/core-lib/models';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';

import { map, take, takeUntil } from 'rxjs/operators';

import {
  ALL_WEEK_REGISTRATION_FILTERS,
  BREEDER_REGISTRATION_PARAMETERS,
} from '../../../../assets/data/weekRegistrationFilters';
import {
  DayRegistration,
  Flock,
  FlockModuleState,
  FlockWeekData,
  FlockWeekDataSet,
  IntervalType,
  ValueRegistration,
  WeekRegistration,
  WeekRegistrationFilters,
  FLOCK_VARIANT,
  FlockVariant,
} from '../../models/flock.model';
import {
  loadFlockWeekChartFilters,
  loadFlocksForAllLocationsRequest,
  loadIsDetailedViewSelectedRequest,
  loadPorphyrioCategoriesRequest,
  loadWeekRegistrationsRequest,
  resetWeekRegistrationFiltersRequest,
  setIsDetailedViewSelectedRequest,
  upsertWeekRegistrationFiltersRequest,
} from '../../store/actions/flock.actions';
import * as actions from '../../store/actions/flock.actions';
import {
  getIsDetailedViewSelected,
  getSelectedFlockWeekData,
  getWeekRegistrationFilters,
} from '../../store/selectors/flock.selectors';

import { FlockOverviewWeekChartSettingsComponent } from '../flock-overview-week-chart-settings/flock-overview-week-chart-settings.component';

import { ImportComponent } from '../import/import.component';

type CsvRegistrationField = keyof ValueRegistration;

const LAYER_CSV_REGISTRATION_FIELDS = [
  'mortality',
  'eggsAmount',
  'eggsAmountSecondKind',
  'eggWeight',
  'forage',
  'water',
  'henWeight',
] as const satisfies readonly CsvRegistrationField[];

const BREEDER_CSV_REGISTRATION_FIELDS = [
  'henMortality',
  'roosterMortality',
  'henWeight',
  'roosterWeight',
  'henFeedIntakeInGrams',
  'roosterFeedIntakeInGrams',
  'henWaterIntakeInLiters',
  'roosterWaterIntakeInLiters',
  'eggCount',
  'hatchingEggCount',
  'eggWeightInGrams',
  'fertilityPercentage',
  'hatchPercentage',
  'lightHours',
  'lightIntensity',
] as const satisfies readonly CsvRegistrationField[];

@Component({
  selector: 'app-flock-overview-week-registration',
  templateUrl: './flock-overview-week-registration.component.html',
  styleUrls: ['./flock-overview-week-registration.component.scss'],
  standalone: false,
})
export class FlockOverviewWeekRegistrationComponent implements OnChanges, OnDestroy {
  @Input() flock: Flock;
  @Input() flockVariant: FlockVariant = FLOCK_VARIANT.Layer;
  @Input() sortBy = 'desc';
  @Input() isMobile: boolean;
  @Input() isLtLgBreakpoint: boolean;
  @Output() editClicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() deleteClicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() sortByChanged: EventEmitter<string> = new EventEmitter<string>();

  private readonly destroy$ = new Subject<void>();
  private readonly selectedFlockWeekData$: Observable<FlockWeekDataSet> =
    this.store.select(getSelectedFlockWeekData);

  // filter possible options
  private readonly kpiFilterOptions$: Observable<NestedFilterOption<string>[]> = of([
    {
      viewValue: this.locale.translate('flock-overview.week-registrations.view-filters.kpis'),
      children: ALL_WEEK_REGISTRATION_FILTERS.kpiFilters.map((kpi) => ({
        value: kpi,
        viewValue: this.locale.translate(`flock-overview.week-registrations.kpis.${kpi}`),
        children: [] as NestedFilterOption<string>[],
        expand: false,
      })),
      expand: true,
    },
  ]);

  private readonly manualInputFilterOptions$: Observable<NestedFilterOption<string>[]> = of([
    {
      viewValue: this.locale.translate(
        'flock-overview.week-registrations.view-filters.manual-input',
      ),
      children: [
        ...ALL_WEEK_REGISTRATION_FILTERS.manualInput.map((manualInputKpi) => ({
          value: manualInputKpi,
          viewValue: this.locale.translate(
            `flock-overview.week-registrations.kpis.${manualInputKpi}`,
          ),
          children: [] as NestedFilterOption<string>[],
          expand: false,
        })),
        {
          value: 'moult',
          viewValue: this.locale.translate('flock-overview.week-registrations.view-filters.moult'),
          children: [] as NestedFilterOption<string>[],
          expand: false,
        },
      ],
      expand: true,
    },
  ]);

  private readonly remarksFilterOptions$: Observable<NestedFilterOption<string>[]> = of([
    {
      value: 'remarks',
      viewValue: this.locale.translate('flock-overview.week-registrations.view-filters.remarks'),
      children: [],
      expand: false,
    },
  ]);

  private readonly breederKpiFilterOptions$: Observable<NestedFilterOption<string>[]> =
    this.selectedFlockWeekData$.pipe(
      map((dataSet) => [
        {
          viewValue: this.locale.translate('flock-overview.week-registrations.view-filters.kpis'),
          children: (dataSet?.dataSeries?.metaData?.parameters ?? [])
            .filter(({ code }) => !BREEDER_REGISTRATION_PARAMETERS.includes(code))
            .map(({ code }) => ({
              value: code,
              viewValue: this.locale.translate(
                `flock-overview.week-registrations.breeder-kpis.${code}`,
              ),
              children: [] as NestedFilterOption<string>[],
              expand: false,
            })),
          expand: true,
        },
      ]),
    );

  private readonly breederManualInputFilterOptions$: Observable<NestedFilterOption<string>[]> = of([
    {
      viewValue: this.locale.translate(
        'flock-overview.week-registrations.view-filters.manual-input',
      ),
      children: BREEDER_REGISTRATION_PARAMETERS.map((parameter) => ({
        value: parameter,
        viewValue: this.locale.translate(
          `flock-overview.week-registrations.breeder-kpis.${parameter}`,
        ),
        children: [] as NestedFilterOption<string>[],
        expand: false,
      })),
      expand: true,
    },
  ]);

  // filter selected options
  weekRegistrationFilters$: Observable<WeekRegistrationFilters> = this.store.select(
    getWeekRegistrationFilters,
  );
  weekRegistrations: WeekRegistration[];
  flockWeekDataSet: FlockWeekDataSet;
  flockWeekData: FlockWeekData[] = [];
  flockWeekDataSeries: DataSeries<number, number>;
  hasWeekRegistrations: boolean;
  itemIndex = 0;
  itemSize = 4;
  weekNavigatorValue: number;
  weekNavigatorDisplayValue = '';
  weekNavigatorOptions: number[] = [];
  filteredWeekNavigatorOptions: number[] = [];
  amountOfHensStartOfWeek?: number;
  isNewRegistration = true;
  currentWeekRegistration: WeekRegistration = {
    weekNr: 0,
    dayRegistrations: [],
    startDay: null,
    endDay: null,
    registrationData: {},
  };
  formGroups: FormGroup[] = [];
  registeredMortality: number[] = [];
  isDetailedView = false;
  viewFilterConfig: FilterConfig<FiltersConfig> = this.createViewFilterConfig();
  initialFlock: Flock;

  constructor(
    private store: Store<FlockModuleState>,
    private dialog: DialogService,
    @Inject(LocaleService) private readonly locale: LocaleService,
  ) {
    this.store.dispatch(loadWeekRegistrationsRequest());
    this.store.dispatch(loadIsDetailedViewSelectedRequest());

    this.selectedFlockWeekData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((weekDataSet: FlockWeekDataSet) => {
        this.flockWeekDataSet = weekDataSet;
        this.flockWeekData = undefined;
        this.flockWeekDataSeries = undefined;
        this.showFilteredWeeks(true);
      });

    this.store
      .select(getIsDetailedViewSelected)
      .pipe(take(1))
      .subscribe((isDetailedViewSelected: boolean) => {
        this.isDetailedView = isDetailedViewSelected;
        this.viewFilterConfig = this.createViewFilterConfig();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleIsDetailedView(): void {
    this.isDetailedView = !this.isDetailedView;
    this.store.dispatch(
      setIsDetailedViewSelectedRequest({
        isDetailedViewSelected: this.isDetailedView,
      }),
    );
  }

  viewFilterDialogSaved(filters: FilterResult<FiltersConfig>): void {
    this.store.dispatch(
      upsertWeekRegistrationFiltersRequest({
        weekRegistrationFilters: {
          kpiFilters: filters.kpis as string[],
          manualInput: (filters.manualInput as string[]).filter((kpi) => kpi !== 'moult'),
          isMoultSelected: (filters.manualInput as string[]).some((kpi) => kpi === 'moult'),
          isRemarksSelected: (filters.remarks as string[])?.length > 0,
        },
      }),
    );
  }

  clearViewFilters(): void {
    this.store.dispatch(resetWeekRegistrationFiltersRequest());
  }

  private createViewFilterConfig(): FilterConfig<FiltersConfig> {
    if (this.flockVariant === FLOCK_VARIANT.Breeder) {
      return {
        settings: false,
        filters: {
          kpis: {
            type: FilterTypes.Tree,
            label: this.locale.translate('flock-overview.week-registrations.view-filters.kpis'),
            value: this.weekRegistrationFilters$.pipe(map((filters) => filters?.kpiFilters)),
            options: this.breederKpiFilterOptions$,
            priority: false,
          },
          manualInput: {
            type: FilterTypes.Tree,
            label: this.locale.translate(
              'flock-overview.week-registrations.view-filters.manual-input',
            ),
            value: this.weekRegistrationFilters$.pipe(map((filters) => filters?.manualInput)),
            options: this.breederManualInputFilterOptions$,
            priority: false,
          },
        },
      };
    }

    return {
      settings: false,
      filters: {
        kpis: {
          type: FilterTypes.Tree,
          label: this.locale.translate('flock-overview.week-registrations.view-filters.kpis'),
          value: this.weekRegistrationFilters$.pipe(map((filters) => filters?.kpiFilters)),
          options: this.kpiFilterOptions$,
          priority: false,
        },
        manualInput: {
          type: FilterTypes.Tree,
          label: this.locale.translate(
            'flock-overview.week-registrations.view-filters.manual-input',
          ),
          value: this.weekRegistrationFilters$.pipe(
            map((filters) =>
              filters.isMoultSelected ? [...filters.manualInput, 'moult'] : filters.manualInput,
            ),
          ),
          options: this.manualInputFilterOptions$,
          priority: false,
        },
        remarks: {
          type: FilterTypes.Tree,
          label: this.locale.translate('flock-overview.week-registrations.view-filters.remarks'),
          value: this.weekRegistrationFilters$.pipe(
            map((filters) => (filters.isRemarksSelected ? ['remarks'] : [])),
          ),
          options: this.remarksFilterOptions$,
          priority: false,
        },
      },
    };
  }

  openChartSettings(): void {
    this.store.dispatch(loadFlocksForAllLocationsRequest());
    this.store.dispatch(loadPorphyrioCategoriesRequest());
    this.store.dispatch(loadFlockWeekChartFilters());

    this.dialog.fullscreenDialog(FlockOverviewWeekChartSettingsComponent, {
      title: this.locale.translate('flock-overview.week-registrations.filters.title'),
      confirmButton: true,
      confirmButtonText: `${this.locale.translate('save')}`,
    });
  }

  onButtonLeft() {
    if (this.hasPrevious()) {
      this.itemIndex--;
    }
    this.weekNavigatorValue = undefined;
    this.showFilteredWeeks();
  }

  onButtonRight() {
    if (this.hasNext()) {
      this.itemIndex++;
    }
    this.weekNavigatorValue = undefined;
    this.showFilteredWeeks();
  }

  hasPrevious(): boolean {
    return this.itemIndex > 0;
  }

  hasNext(): boolean {
    return (
      !!this.flockWeekData &&
      this.flockWeekDataSet.data.length - 1 - this.itemSize >= this.itemIndex
    );
  }

  private showFilteredWeeks(applyWeekNavigator = false) {
    if (this.flockWeekDataSet !== undefined && this.flockWeekDataSet.data !== undefined) {
      this.flockWeekDataSeries = { ...this.flockWeekDataSet.dataSeries };
      this.hasWeekRegistrations = this.flockWeekDataSet.data?.length > 0;

      // Set size to 2 when mobile view, to 3 when tablet view or to 4 when desktop view.
      if (this.isMobile) {
        this.itemSize = 2;
      } else if (this.isLtLgBreakpoint) {
        this.itemSize = 3;
      } else {
        this.itemSize = 4;
      }

      const sortedFlockWeekData = this.getSortedFlockWeekData();
      this.updateWeekNavigatorOptions(sortedFlockWeekData);

      if (
        this.weekNavigatorValue !== undefined &&
        !this.weekNavigatorOptions.includes(this.weekNavigatorValue)
      ) {
        this.weekNavigatorValue = undefined;
      }

      if (applyWeekNavigator && this.weekNavigatorValue !== undefined) {
        this.itemIndex = this.getWeekWindowStartIndex(sortedFlockWeekData, this.weekNavigatorValue);
      }

      // Reset start position for pagination.
      this.itemIndex = this.getClampedItemIndex(sortedFlockWeekData);

      // Now take only the parts needed to be presented with pagination.
      const eIndex = Math.min(this.itemIndex + this.itemSize - 1, sortedFlockWeekData.length - 1);
      this.flockWeekData = sortedFlockWeekData.slice(this.itemIndex, eIndex + 1);

      this.updateWeekNavigatorDisplayValue();
      this.filterWeekNavigatorOptions(this.weekNavigatorDisplayValue);
    }
  }

  ngOnChanges(): void {
    this.viewFilterConfig = this.createViewFilterConfig();
    this.showFilteredWeeks(true);
  }

  onWeekNavigatorInput(event: Event): void {
    this.weekNavigatorDisplayValue = (event.target as HTMLInputElement).value;
    this.weekNavigatorValue = this.parseWeekNavigatorValue(this.weekNavigatorDisplayValue);
    this.filterWeekNavigatorOptions(this.weekNavigatorDisplayValue);
  }

  showAllWeekNavigatorOptions(): void {
    this.filteredWeekNavigatorOptions = [...this.weekNavigatorOptions];
  }

  onWeekNavigatorOptionSelected(value: string): void {
    const weekNr = this.parseWeekNavigatorValue(value);
    if (!this.weekNavigatorOptions.includes(weekNr)) {
      this.resetWeekNavigatorToVisibleWeek();
      return;
    }

    this.selectNavigatorWeek(weekNr);
  }

  jumpToNavigatorWeek(): void {
    const normalizedWeekNr = Math.round(Number(this.weekNavigatorValue));

    if (
      !Number.isFinite(normalizedWeekNr) ||
      !this.weekNavigatorOptions.includes(normalizedWeekNr)
    ) {
      this.resetWeekNavigatorToVisibleWeek();
      return;
    }

    this.selectNavigatorWeek(normalizedWeekNr);
  }

  private selectNavigatorWeek(weekNr: number): void {
    this.weekNavigatorValue = weekNr;
    this.weekNavigatorDisplayValue = this.formatWeekNavigatorValue(weekNr);
    this.itemIndex = this.getWeekWindowStartIndex(this.getSortedFlockWeekData(), weekNr);
    this.showFilteredWeeks();
  }

  private getSortedFlockWeekData(): FlockWeekData[] {
    const sortedFlockWeekData = [...this.flockWeekDataSet.data];
    if (this.sortBy === 'desc') {
      sortedFlockWeekData.sort((w1, w2) => (w1.week < w2.week ? 1 : -1));
    } else {
      sortedFlockWeekData.sort((w1, w2) => (w1.week > w2.week ? 1 : -1));
    }
    return sortedFlockWeekData;
  }

  private getWeekWindowStartIndex(sortedFlockWeekData: FlockWeekData[], weekNr: number): number {
    const targetIndex = this.getWeekTargetIndex(sortedFlockWeekData, weekNr);
    const centeredStartIndex = targetIndex - Math.floor(this.itemSize / 2);
    const maxStartIndex = Math.max(sortedFlockWeekData.length - this.itemSize, 0);

    return Math.min(Math.max(centeredStartIndex, 0), maxStartIndex);
  }

  private getWeekTargetIndex(sortedFlockWeekData: FlockWeekData[], weekNr: number): number {
    const exactIndex = sortedFlockWeekData.findIndex((weekData) => weekData.week === weekNr);
    if (exactIndex >= 0) {
      return exactIndex;
    }

    if (this.sortBy === 'desc') {
      const insertionIndex = sortedFlockWeekData.findIndex((weekData) => weekData.week <= weekNr);
      return insertionIndex >= 0 ? insertionIndex : sortedFlockWeekData.length - 1;
    }

    const insertionIndex = sortedFlockWeekData.findIndex((weekData) => weekData.week >= weekNr);
    return insertionIndex >= 0 ? insertionIndex : sortedFlockWeekData.length - 1;
  }

  private getClampedItemIndex(sortedFlockWeekData: FlockWeekData[]): number {
    const maxStartIndex = Math.max(sortedFlockWeekData.length - this.itemSize, 0);
    return Math.min(Math.max(this.itemIndex, 0), maxStartIndex);
  }

  private updateWeekNavigatorDisplayValue(): void {
    if (this.weekNavigatorValue !== undefined) {
      this.weekNavigatorDisplayValue = this.formatWeekNavigatorValue(this.weekNavigatorValue);
    } else if (this.flockWeekData?.length > 0) {
      this.weekNavigatorDisplayValue = this.formatWeekNavigatorValue(this.flockWeekData[0].week);
    }
  }

  formatWeekNavigatorValue(weekNr: number): string {
    return `${this.locale.translate('week-registration.week')} ${weekNr}`;
  }

  private parseWeekNavigatorValue(value: string): number {
    const weekMatch = /\d+/.exec(value);
    return weekMatch ? Number(weekMatch[0]) : Number(value);
  }

  private updateWeekNavigatorOptions(sortedFlockWeekData: FlockWeekData[]): void {
    this.weekNavigatorOptions = sortedFlockWeekData
      .map((weekData) => weekData.week)
      .filter((week, index, weeks) => weeks.indexOf(week) === index);
  }

  private resetWeekNavigatorToVisibleWeek(): void {
    this.weekNavigatorValue = undefined;
    this.updateWeekNavigatorDisplayValue();
    this.filterWeekNavigatorOptions(this.weekNavigatorDisplayValue);
  }

  private filterWeekNavigatorOptions(value: string): void {
    const searchValue = value?.toLowerCase().trim() ?? '';

    if (!searchValue) {
      this.filteredWeekNavigatorOptions = [...this.weekNavigatorOptions];
      return;
    }

    this.filteredWeekNavigatorOptions = this.weekNavigatorOptions.filter((week) =>
      this.formatWeekNavigatorValue(week).toLowerCase().includes(searchValue),
    );
  }

  selectedIntervalTypeOption: IntervalType;

  selectOptions = [
    {
      value: IntervalType.Day,
      name: this.locale.translate('week-registration.daily-registrations'),
    },
    {
      value: IntervalType.Week,
      name: this.locale.translate('week-registration.weekly-registrations'),
    },
  ];

  csvDelimiter = ',';
  csvDateFormat = 'MM-dd-yyyy';
  readonly flockVariantEnum = FLOCK_VARIANT;

  parseValue(value: string): number {
    return !isNaN(parseFloat(value?.replace(/,/g, '.')))
      ? parseFloat(value?.replace(/,/g, '.'))
      : null;
  }

  private getCsvRegistrationFields(): readonly CsvRegistrationField[] {
    return this.flockVariant === FLOCK_VARIANT.Breeder
      ? BREEDER_CSV_REGISTRATION_FIELDS
      : LAYER_CSV_REGISTRATION_FIELDS;
  }

  private getCsvColumnNames(): string[] {
    return [
      `${this.locale.translate('flock-overview.week-registrations.date')} (${this.csvDateFormat})`,
      this.selectedIntervalTypeOption === IntervalType.Day
        ? this.locale.translate('flock-overview.week-registrations.days')
        : this.locale.translate('flock-overview.week-registrations.weeks'),
      ...this.getCsvRegistrationFields().map((field) =>
        this.locale.translate(`week-registration.${field}`),
      ),
    ];
  }

  downloadTemplate() {
    this.dialog
      .select({
        options: this.selectOptions,
        selected: this.selectedIntervalTypeOption,
        emptyText: 'No options available',
        pushHistoryState: false,
      })
      .subscribe(({ status, result }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          this.selectedIntervalTypeOption = result;
          let csvContent = 'data:text/csv;charset=utf-8,';
          const columnNamesCSV = this.getCsvColumnNames();

          const row = columnNamesCSV.join(this.csvDelimiter);
          csvContent += row + '\r\n';

          if (this.selectedIntervalTypeOption === IntervalType.Week) {
            csvContent += this.AddWeeklyRowsInTemplate(this.flock);
          } else if (this.selectedIntervalTypeOption === IntervalType.Day) {
            csvContent += this.AddDailyRowsInTemplate(this.flock);
          }

          const encodedUri = encodeURI(csvContent);
          const link = document.createElement('a');
          link.setAttribute('type', 'hidden');
          link.href = encodedUri;
          const templateVariant =
            this.flockVariant === FLOCK_VARIANT.Breeder ? 'breeders' : 'layers';
          link.download = `poultrynext-${templateVariant}_performance-template_${
            this.selectedIntervalTypeOption
          }_${this.flock.name}_${formatDate(new Date(), this.csvDateFormat, 'en').trim()}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      });
  }

  private AddWeeklyRowsInTemplate(flock: Flock): string {
    let rows = '';
    const birthDate = new Date(flock.arrival.chickBirthDate);
    const arrivalDate = new Date(flock.arrival.arrivalDate);

    const maximumAllowedDate = this.flock.departure?.departureDate
      ? new Date(this.flock.departure.departureDate)
      : new Date();

    const weekStartDate = arrivalDate;

    const weekNumberStart = this.WeeksInBetween(birthDate, arrivalDate);
    const weekNumberEnd = this.WeeksInBetween(birthDate, maximumAllowedDate);

    for (let i = weekNumberStart; i <= weekNumberEnd; i++) {
      const csvWeekNumber = i;
      const csvDate = formatDate(weekStartDate, this.csvDateFormat, 'en');

      const intervalValue = [csvDate, csvWeekNumber];

      const row = intervalValue.join(this.csvDelimiter);
      rows += row + '\r\n';

      this.addDays(weekStartDate, 7);
    }

    return rows;
  }

  private AddDailyRowsInTemplate(flock: Flock): string {
    let rows = '';
    const birthDate = new Date(flock.arrival.chickBirthDate);
    const arrivalDate = new Date(flock.arrival.arrivalDate);

    const maximumAllowedDate = this.flock.departure?.departureDate
      ? new Date(this.flock.departure.departureDate)
      : new Date();

    const dayNumberStart = this.DaysInBetween(birthDate, arrivalDate);
    const dayNumberEnd = this.DaysInBetween(birthDate, maximumAllowedDate);

    for (let i = dayNumberStart; i <= dayNumberEnd; i++) {
      const csvDayNumber = i;
      const csvDate = formatDate(arrivalDate, this.csvDateFormat, 'en');

      const intervalValue = [csvDate, csvDayNumber];

      const row = intervalValue.join(this.csvDelimiter);
      rows += row + '\r\n';

      this.addDays(arrivalDate, 1);
    }

    return rows;
  }

  private WeeksInBetween(startDate: Date, endDate: Date): number {
    const msInWeek = 7 * 24 * 60 * 60 * 1000;
    return Math.ceil(Math.abs(Number(endDate) - Number(startDate)) / msInWeek);
  }

  private DaysInBetween(startDate: Date, endDate: Date): number {
    const msInDay = 24 * 60 * 60 * 1000;
    return Math.floor(Math.abs(Number(endDate) - Number(startDate)) / msInDay);
  }

  private addDays(date: Date, numberOfDays: number): Date {
    return new Date(date.setDate(date.getDate() + numberOfDays));
  }

  uploadCSV() {
    this.dialog
      .dialog(ImportComponent, {
        intervalTypes: [IntervalType.Day, IntervalType.Week],
        title: this.locale.translate(
          'flock-overview.week-registrations.weekregistration-upload-csv-title',
        ),
        flock: this.flock,
        weekNr: 0,
        cancelButtonText: this.locale.translate('cancel'),
        confirmButtonText: this.locale.translate('save'),
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm && result.result?.file) {
          const reader: FileReader = new FileReader();
          reader.onload = (e) => {
            const lines: string[] = (e.target.result as string).split('\r\n');
            let finalFlock: Flock;
            this.weekRegistrations = this.flock.weekRegistrations;

            this.csvDelimiter = this.guessDelimiter(e.target.result as string, [',', ';']);

            if (result.result?.intervalType === IntervalType.Week) {
              finalFlock = this.uploadWeeklyRegistrations(lines);
            } else {
              finalFlock = this.uploadDailyRegistration(lines);
            }
            this.store.dispatch(
              actions.upsertFlockRequest({ request: finalFlock, variant: this.flockVariant }),
            );
          };
          reader.readAsText(result.result?.file);
        }
      });
  }

  private getWeekRegistrationByWeekNumberInFlock(weekNumber: number): WeekRegistration {
    let result: WeekRegistration = null;
    this.flock.weekRegistrations.forEach((week) => {
      if (week.weekNr === weekNumber) {
        result = week;
      }
    });

    return result;
  }

  private getWeekRegistrationByDayInFlock(dayNumber: number): WeekRegistration {
    let result: WeekRegistration = null;
    this.flock.weekRegistrations.forEach((week) => {
      if (week.startDay <= dayNumber && week.endDay >= dayNumber) {
        result = week;
      }
    });

    return result;
  }

  private getNextWeekRegistrationStartDay(): number {
    if (this.flock.weekRegistrations.length > 0) {
      return this.flock.weekRegistrations[this.flock.weekRegistrations.length - 1].endDay + 1;
    }

    return this.DaysInBetween(
      new Date(this.flock.arrival.chickBirthDate),
      new Date(this.flock.arrival.arrivalDate),
    );
  }

  private getNextWeekRegistrationWeekNumber(): number {
    if (this.flock.weekRegistrations.length > 0) {
      return this.flock.weekRegistrations[this.flock.weekRegistrations.length - 1].weekNr + 1;
    }
  }

  private allLineValuesAreEmpty(lines: string[], index: number): boolean {
    for (let i = index; i < lines.length; i++) {
      if (lines[i].length === 0) {
        return true;
      }

      const lineValues = lines[i]?.split(this.csvDelimiter);
      if (lineValues && !this.lineValuesAreEmpty(lineValues)) {
        return false;
      }
    }
  }

  private uploadWeeklyRegistrations(lines: string[]): Flock {
    for (let lineNumber = 1; lineNumber < lines.length; lineNumber++) {
      const lineValues = lines[lineNumber].split(this.csvDelimiter);
      const csvWeekNumber = this.parseValue(lineValues[1]);

      let weekRegistration = this.getWeekRegistrationByWeekNumberInFlock(csvWeekNumber);
      if (weekRegistration == null) {
        weekRegistration = this.createEmptyWeekRegistration(
          csvWeekNumber,
          this.getNextWeekRegistrationStartDay(),
        );
      }

      this.updateWeekRegistrationsWithValuesInCsv(weekRegistration, lineValues);

      if (this.allLineValuesAreEmpty(lines, lineNumber + 1)) {
        return this.flock;
      }
    }

    return this.flock;
  }

  private createEmptyDayRegistrations(startDay: number): DayRegistration[] {
    const result = [];

    for (let i = 0; i <= 6; i++) {
      result.push({
        day: startDay + i,
        registrationData: {},
      } as DayRegistration);
    }

    return result;
  }

  private createEmptyWeekRegistration(weekNumber: number, startDay: number): WeekRegistration {
    const endDay = startDay + 6;
    return {
      dayRegistrations: this.createEmptyDayRegistrations(startDay),
      weekNr: weekNumber,
      registrationData: {},
      startDay,
      endDay,
    } as WeekRegistration;
  }

  private updateRegistrationValue(csvValue: string): number {
    const csvNumber = this.parseValue(csvValue);
    if (csvNumber !== null) {
      return csvNumber;
    }
  }

  private updateRegistrationDataWithValuesInCsv(
    registrationData: ValueRegistration,
    lineValues: string[],
  ): void {
    this.getCsvRegistrationFields().forEach((field, index) => {
      const csvValue = this.updateRegistrationValue(lineValues[index + 2]);
      if (csvValue !== undefined) {
        (registrationData as Record<string, number>)[field] = csvValue;
      }
    });
  }

  private updateDayRegistrationsWithValuesInCsv(
    weekRegistration: WeekRegistration,
    lineValues: string[],
  ): void {
    const csvDay = this.parseValue(lineValues[1]);
    const updatedWeekRegistration: WeekRegistration = JSON.parse(JSON.stringify(weekRegistration));

    for (let i = 0; i < updatedWeekRegistration.dayRegistrations.length; i++) {
      const updatedRegistrationData = updatedWeekRegistration.dayRegistrations[i].registrationData;

      const day = weekRegistration.dayRegistrations[i].day;

      if (day === csvDay) {
        this.updateRegistrationDataWithValuesInCsv(updatedRegistrationData, lineValues);
        break;
      }
    }

    this.flock.weekRegistrations = [
      ...this.flock.weekRegistrations.filter(
        (week) => week.startDay !== updatedWeekRegistration.startDay,
      ),
      updatedWeekRegistration,
    ];
  }

  private updateWeekRegistrationsWithValuesInCsv(
    weekRegistration: WeekRegistration,
    lineValues: string[],
  ): void {
    const weekRegistrationWeekNumber = weekRegistration.weekNr;
    const csvWeek = this.parseValue(lineValues[1]);
    if (csvWeek <= weekRegistrationWeekNumber) {
      const updatedWeekRegistration: WeekRegistration = JSON.parse(
        JSON.stringify(weekRegistration),
      );

      this.updateRegistrationDataWithValuesInCsv(
        updatedWeekRegistration.registrationData,
        lineValues,
      );

      this.flock.weekRegistrations = [
        ...this.flock.weekRegistrations.filter(
          (week) => week.startDay !== updatedWeekRegistration.startDay,
        ),
        updatedWeekRegistration,
      ];
    }
  }

  private uploadDailyRegistration(lines: string[]): Flock {
    for (let lineNumber = 1; lineNumber < lines.length; lineNumber++) {
      const lineValues = lines[lineNumber].split(this.csvDelimiter);
      const csvDay = this.parseValue(lineValues[1]);

      let weekRegistration = this.getWeekRegistrationByDayInFlock(csvDay);
      if (weekRegistration == null) {
        weekRegistration = this.createEmptyWeekRegistration(
          this.getNextWeekRegistrationWeekNumber(),
          this.getNextWeekRegistrationStartDay(),
        );
      }

      this.updateDayRegistrationsWithValuesInCsv(weekRegistration, lineValues);

      if (this.allLineValuesAreEmpty(lines, lineNumber + 1)) {
        return this.flock;
      }
    }

    return this.flock;
  }

  private guessDelimiter(text: string, possibleDelimiters: string[]) {
    return possibleDelimiters.find(weedOut);

    function weedOut(delimiter: string) {
      let cache = -1;
      return text.split('\n').every(checkLength);

      function checkLength(line: string) {
        if (!line) {
          return true;
        }

        const length = line.split(delimiter).length;
        if (cache < 0) {
          cache = length;
        }
        return cache === length && length > 1;
      }
    }
  }

  private lineValuesAreEmpty(lineValues: string[]) {
    return this.getCsvRegistrationFields().every(
      (_field, index) => this.parseValue(lineValues[index + 2]) === null,
    );
  }
}
